import { useState, useEffect, useCallback, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallStatus =
  | 'idle'
  | 'prompting'
  | 'accepted'
  | 'installed'
  | 'shortcut-suspected';

const ATTEMPT_KEY = 'miamsecur-install-attempted';
const ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [installStatus, setInstallStatus] = useState<InstallStatus>('idle');
  const [wasInstallSilentlyFailed, setWasInstallSilentlyFailed] = useState(false);

  const promptCountRef = useRef(0);
  const acceptedAtRef = useRef<number | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Détecter si déjà installé
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: window-controls-overlay)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsInstalled(isStandalone);

    // Détection plateforme
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
    const android = /Android/i.test(ua);
    setIsIOS(iOS);
    setIsAndroid(android);
    setIsDesktop(!iOS && !android);

    // Détection d'un échec silencieux d'installation précédent
    try {
      const raw = localStorage.getItem(ATTEMPT_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Number.isFinite(ts) && Date.now() - ts < ATTEMPT_TTL_MS && !isStandalone) {
          setWasInstallSilentlyFailed(true);
        } else if (isStandalone || !Number.isFinite(ts) || Date.now() - ts >= ATTEMPT_TTL_MS) {
          // Nettoyer le flag obsolète ou résolu
          localStorage.removeItem(ATTEMPT_KEY);
        }
      }
    } catch {
      // localStorage indisponible — ignorer
    }

    // beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);

      promptCountRef.current += 1;
      // Si on l'a accepté il y a peu et que Chrome redéclenche => raccourci suspect
      if (acceptedAtRef.current && Date.now() - acceptedAtRef.current < 30_000) {
        setInstallStatus('shortcut-suspected');
        if (watchdogRef.current) {
          clearTimeout(watchdogRef.current);
          watchdogRef.current = null;
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // appinstalled => succès confirmé
    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsInstallable(false);
      setInstallStatus('installed');
      setWasInstallSilentlyFailed(false);
      acceptedAtRef.current = null;
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      try {
        localStorage.removeItem(ATTEMPT_KEY);
      } catch {
        // ignorer
      }
    };

    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;

    setInstallStatus('prompting');
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      acceptedAtRef.current = Date.now();
      setInstallStatus('accepted');
      setDeferredPrompt(null);
      setIsInstallable(false);

      try {
        localStorage.setItem(ATTEMPT_KEY, Date.now().toString());
      } catch {
        // ignorer
      }

      // Watchdog : si pas d'événement appinstalled dans 8 s, suspecter raccourci
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      watchdogRef.current = setTimeout(() => {
        setInstallStatus((current) => (current === 'installed' ? current : 'shortcut-suspected'));
      }, 8000);

      return true;
    }

    setInstallStatus('idle');
    return false;
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isAndroid,
    isDesktop,
    canShowButton: !isInstalled,
    promptInstall,
    installStatus,
    wasInstallSilentlyFailed,
  };
};
